import React from 'react';

export type CustomerStage = 'ups' | 'gls' | 'fbo' | 'dhl' | 'invoice' | 'remits';

export type Customer = {
  id: string;
  name: string;
  stages: CustomerStage[];
};

export const CUSTOMERS: Customer[] = [
  {
    id: 'specialized-bike',
    name: 'Specialized Bike',
    stages: ['ups', 'gls', 'invoice', 'remits'],
  },
  {
    id: 'twinmed',
    name: 'TwinMed',
    stages: ['invoice', 'remits'],
  },
  {
    id: 'inexpress',
    name: 'InExpress',
    stages: ['dhl', 'invoice'],
  },
  {
    id: 'moss-distributing',
    name: 'Moss Distributing',
    stages: ['fbo', 'invoice'],
  },
  {
    id: 'tencarva',
    name: 'Tencarva',
    stages: ['ups', 'invoice'],
  },
  {
    id: 'empire-comfort-systems',
    name: 'Empire Comfort Systems',
    stages: ['ups', 'invoice'],
  },
  {
    id: 'mossberg',
    name: 'Mossberg',
    stages: ['ups', 'fbo', 'invoice'],
  },
  {
    id: 'gd-energy',
    name: 'GD Energy',
    stages: ['ups', 'fbo', 'invoice'],
  },
  {
    id: 'manitoba-harvest',
    name: 'Manitoba Harvest',
    stages: ['ups', 'invoice'],
  },
  {
    id: 'millennium',
    name: 'Millennium',
    stages: ['ups', 'invoice'],
  },
  {
    id: 'pourri',
    name: 'Pourri',
    stages: ['ups', 'invoice'],
  },
];

export const DEFAULT_CUSTOMER = CUSTOMERS[0];

const STORAGE_KEY = 'bg.parcel.selectedCustomerId';

const loadPersistedCustomer = (): Customer => {
  try {
    const id = window.localStorage.getItem(STORAGE_KEY);
    if (id) {
      const found = CUSTOMERS.find((c) => c.id === id);
      if (found) return found;
    }
  } catch {
    /* localStorage may be unavailable */
  }
  return DEFAULT_CUSTOMER;
};

type CustomerContextValue = {
  customer: Customer;
  setCustomer: (next: Customer) => void;
  customers: Customer[];
};

const CustomerContext = React.createContext<CustomerContextValue>({
  customer: DEFAULT_CUSTOMER,
  setCustomer: () => undefined,
  customers: CUSTOMERS,
});

export const CustomerProvider = ({ children }: { children: React.ReactNode }) => {
  const [customer, setCustomerState] = React.useState<Customer>(loadPersistedCustomer);

  const setCustomer = React.useCallback((next: Customer) => {
    setCustomerState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next.id);
    } catch {
      /* localStorage may be unavailable */
    }
  }, []);

  const value = React.useMemo(
    () => ({ customer, setCustomer, customers: CUSTOMERS }),
    [customer, setCustomer]
  );

  return (
    <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>
  );
};

export const useCustomer = (): CustomerContextValue => React.useContext(CustomerContext);
